import React, { useState, useMemo } from 'react'
import { Layout, Card, Typography, Space, Button, Input, DatePicker, Row, Col, App, Statistic, message, Table } from 'antd'
import {
  FileTextOutlined,
  DownloadOutlined,
  PrinterOutlined,
  BarChartOutlined,
  TruckOutlined,
  UserOutlined,
  FileProtectOutlined,
  TagOutlined,
  AlertOutlined,
  CalculatorOutlined,
  BoxPlotOutlined,
  DatabaseOutlined,
  SettingOutlined,
  CodeOutlined
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '../../contexts/ThemeContext'
import { useAuth } from '../../contexts/AuthContext'
import dayjs from 'dayjs'
import './Relatorios.css'
import { Bar, Chart as ReactChart } from 'react-chartjs-2'
import { generatePrintableHTML } from '../../relatorios/utils/printUtils'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  BarController,
  LineController,
  Title as ChartTitle,
  Tooltip,
  Legend
} from 'chart.js'
import * as XLSX from 'xlsx'
import { MatrixController, MatrixElement } from 'chartjs-chart-matrix'
import { relatorios } from '../../services/api'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  BarController,
  LineController,
  ChartTitle,
  Tooltip,
  Legend
)
ChartJS.register(MatrixController, MatrixElement)

const { Content } = Layout
const { Title, Text } = Typography

// Componente para exibir cards de métricas (estilo moderno)
const MetricCard = ({ title, value, icon, color, theme }) => (
  <Card
    style={{
      background: theme === 'dark'
        ? 'linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.04))'
        : 'linear-gradient(135deg, #ffffff, #f7f7fb)',
      border: theme === 'dark' ? '1px solid #3b3b3b' : '1px solid #eaeaea',
      borderRadius: '10px',
      height: '72px',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: theme === 'dark'
        ? '0 6px 12px rgba(0,0,0,0.35)'
        : '0 6px 12px rgba(0,0,0,0.08)'
    }}
    styles={{
      body: {
        padding: '10px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }
    }}
  >
    <div style={{
      position: 'absolute',
      top: '8px',
      right: '8px',
      fontSize: '18px',
      color: color,
      opacity: 0.2
    }}>
      {icon}
    </div>
    <div>
      <Text style={{
        color: theme === 'dark' ? '#eaeaea' : '#1f2937',
        fontSize: '11px',
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        marginBottom: '4px'
      }}>
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '18px',
          height: '18px',
          borderRadius: '6px',
          backgroundColor: color,
          opacity: 0.18
        }}>
          <span style={{ color: color, opacity: 0.9, fontSize: '12px' }}>{icon}</span>
        </span>
        {title}
      </Text>
      <Text style={{
        color: theme === 'dark' ? '#ffffff' : '#111827',
        fontSize: '16px',
        fontWeight: 800,
        display: 'block',
        textAlign: 'center'
      }}>
        {value}
      </Text>
    </div>
    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '2px', backgroundColor: color, opacity: 0.35 }} />
  </Card>
)

const MetricsGrid = ({ metrics, theme, onExport }) => {
  if (!metrics) return null;
  return (
    <div style={{ marginTop: '12px' }}>
      <Row gutter={[12, 12]}>
        <Col xs={24} sm={12} md={8} lg={6} xl={4} xxl={3}>
          <MetricCard
            title="Total Veículos"
            value={metrics.totalVeiculos}
            icon={<TruckOutlined />}
            color="#6366f1"
            theme={theme}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4} xxl={3}>
          <MetricCard
            title="Total Clientes"
            value={metrics.totalClientes}
            icon={<UserOutlined />}
            color="#10b981"
            theme={theme}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4} xxl={3}>
          <MetricCard
            title="Total Notas"
            value={metrics.totalNotas}
            icon={<FileProtectOutlined />}
            color="#f59e0b"
            theme={theme}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4} xxl={3}>
          <MetricCard
            title="Notas Manuais"
            value={metrics.notasManuais}
            icon={<CalculatorOutlined />}
            color="#8b5cf6"
            theme={theme}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4} xxl={3}>
          <MetricCard
            title="Total Etiquetas"
            value={metrics.totalEtiquetas}
            icon={<TagOutlined />}
            color="#06b6d4"
            theme={theme}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4} xxl={3}>
          <MetricCard
            title="Notas Impressas"
            value={metrics.notasImpressas}
            icon={<PrinterOutlined />}
            color="#8b5cf6"
            theme={theme}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4} xxl={3}>
          <MetricCard
            title="Notas Pendentes"
            value={metrics.notasPendentes}
            icon={<AlertOutlined />}
            color="#ef4444"
            theme={theme}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4} xxl={3}>
          <MetricCard
            title="Total Peso (kg)"
            value={metrics.totalPeso}
            icon={<CalculatorOutlined />}
            color="#64748b"
            theme={theme}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4} xxl={3}>
          <MetricCard
            title="Total Volumes"
            value={metrics.totalVolumes}
            icon={<BoxPlotOutlined />}
            color="#8b5cf6"
            theme={theme}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4} xxl={3}>
          <MetricCard
            title="Total Impressoras"
            value={metrics.totalImpressoras}
            icon={<PrinterOutlined />}
            color="#ec4899"
            theme={theme}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4} xxl={3}>
          <MetricCard
            title="Total Marcas"
            value={metrics.totalMarcas}
            icon={<DatabaseOutlined />}
            color="#10b981"
            theme={theme}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4} xxl={3}>
          <MetricCard
            title="Total Modelos"
            value={metrics.totalModelos}
            icon={<SettingOutlined />}
            color="#f59e0b"
            theme={theme}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4} xxl={3}>
          <MetricCard
            title="Total Emuladores"
            value={metrics.totalEmuladores}
            icon={<CodeOutlined />}
            color="#6366f1"
            theme={theme}
          />
        </Col>
      </Row>

      <div style={{
        display: 'flex',
        justifyContent: 'center',
        marginTop: '12px'
      }}>
      </div>
    </div>
  )
}

const Relatorios = ({ embedded = false }) => {
  const navigate = useNavigate()
  const { currentTheme } = useTheme()
  const { user } = useAuth()
  const { message } = App.useApp()
  const [sidebarVisible, setSidebarVisible] = useState(false)

  // Estados para os filtros
  const [empresaInicial, setEmpresaInicial] = useState('1')
  const [empresaFinal, setEmpresaFinal] = useState('999')
  const [dataInicial, setDataInicial] = useState(dayjs().format('YYYY-MM-DD'))
  const [dataFinal, setDataFinal] = useState(dayjs().format('YYYY-MM-DD'))
  const [loading, setLoading] = useState(false)

  // Estado para armazenar dados do relatório
  const [dadosRelatorio, setDadosRelatorio] = useState([])
  const [metricas, setMetricas] = useState(null)

  // Top 10 memos (dentro do componente)
  const topVeiculos = useMemo(() => {
    const agg = aggregateBy(dadosRelatorio, (item) => item.CARRO || item.veiculo || item.carro)
    return agg.sort((a, b) => b.etiquetasImp - a.etiquetasImp).slice(0, 10)
  }, [dadosRelatorio])

  const topClientes = useMemo(() => {
    const agg = aggregateBy(
      dadosRelatorio,
      (item) => item.REMETENTE || item.destinatario || item.CLIENTE || item.cliente || item.cnpj || item.CNPJ
    )
    return agg.sort((a, b) => b.etiquetasImp - a.etiquetasImp).slice(0, 10)
  }, [dadosRelatorio])

  const topImpressoras = useMemo(() => {
    const agg = aggregateBy(dadosRelatorio, (item) => item.impressora || item.PRINTER || item.printer)
    return agg.sort((a, b) => b.etiquetasImp - a.etiquetasImp).slice(0, 10)
  }, [dadosRelatorio])
  const dailyAgg = useMemo(() => groupByDate(dadosRelatorio), [dadosRelatorio])
  // Função para alternar visibilidade da sidebar em mobile
  const handleMobileMenuClick = () => {
    setSidebarVisible(!sidebarVisible)
  }

  // Função para calcular métricas dos dados
  const calcularMetricas = (dados) => {
    if (!dados || dados.length === 0) {
      return {
        totalVeiculos: 0,
        totalClientes: 0,
        totalNotas: 0,
        totalEtiquetas: 0,
        notasImpressas: 0,
        notasPendentes: 0,
        totalPeso: 0,
        totalVolumes: 0,
        totalImpressoras: 0,
        totalMarcas: 0,
        totalModelos: 0,
        totalEmuladores: 0
      }
    }

    // Calcular métricas baseadas nos dados
    const veiculosUnicos = new Set(dados.map(item => item.CARRO)).size
    const clientesUnicos = new Set(dados.map(item => item.cnpj || item.CNPJ)).size
    const totalNotas = dados.length
    const notasManuais = dados.filter(item => item.inclusao === 'MANUAL').length
    const totalEtiquetas = dados.reduce((sum, item) => sum + (item.TTL_ETIQUETAS_IMP || 0), 0)
    const notasImpressas = dados.filter(item => (item.TTL_ETIQUETAS_IMP || 0) > 0).length
    const notasPendentes = dados.filter(item => (item.TTL_ETIQUETAS_SEM || 0) > 0).length
    const totalPeso = dados.reduce((sum, item) => sum + parseFloat(item.PESO || 0), 0)
    const totalVolumes = dados.reduce((sum, item) => sum + (item.VOLUMES || 0), 0)
    const impressorasUnicas = new Set(dados.map(item => item.impressora).filter(Boolean)).size
    const marcasUnicas = new Set(dados.map(item => item.marca).filter(Boolean)).size
    const modelosUnicos = new Set(dados.map(item => item.modelo).filter(Boolean)).size
    const emuladoresUnicos = new Set(dados.map(item => item.emulador).filter(Boolean)).size

    return {
      totalVeiculos: veiculosUnicos,
      totalClientes: clientesUnicos,
      totalNotas: totalNotas,
      notasManuais: notasManuais,
      totalEtiquetas: totalEtiquetas,
      notasImpressas: notasImpressas,
      notasPendentes: notasPendentes,
      totalPeso: totalPeso.toFixed(2),
      totalVolumes: totalVolumes,
      totalImpressoras: impressorasUnicas,
      totalMarcas: marcasUnicas,
      totalModelos: modelosUnicos,
      totalEmuladores: emuladoresUnicos
    }
  }

  // Função para buscar dados do relatório usando o serviço centralizado de API
  const buscarDadosRelatorio1 = async (empresaInicial, empresaFinal, dataInicial, dataFinal) => {
    try {
      const response = await relatorios.buscarDadosRelatorio1({
        empresa_inicial: empresaInicial,
        empresa_final: empresaFinal,
        data_inicial: dataInicial,
        data_final: dataFinal
      });

      // Extrair dados dos registros
      const registros = response.data || [];
      setDadosRelatorio(registros);

      // Calcular métricas
      const metricasCalculadas = calcularMetricas(registros);
      setMetricas(metricasCalculadas);

      return response;
    } catch (error) {
      console.error('Erro na requisição do relatório 1:', error);
      message.error(`Erro na requisição: ${error.message}`);
      throw error;
    }
  };

  // Função para gerar relatório
  const handleGerarRelatorio = async () => {
    if (!dataInicial || !dataFinal) {
      message.warning('Por favor, selecione as datas inicial e final');
      return;
    }

    setLoading(true);
    try {
      const dados = await buscarDadosRelatorio1(empresaInicial, empresaFinal, dataInicial, dataFinal);

      // Aqui você pode implementar a lógica para exibir ou processar os dados
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      message.error('Erro ao gerar relatório');
    } finally {
      setLoading(false);
    }
  }

  // Função para imprimir relatório
  const handleImprimir = async () => {
    try {
      // Mostra loading
      const loadingMessage = message.loading('Gerando relatório com gráficos...', 0);

      // Calcula o período para o relatório
      const periodo = dataInicial && dataFinal
        ? `${dayjs(dataInicial).format('DD/MM/YYYY')} a ${dayjs(dataFinal).format('DD/MM/YYYY')}`
        : 'Período não especificado';

      // Prepara informações dos filtros
      const filtros = {
        empresaInicial,
        empresaFinal,
        dataInicial: dayjs(dataInicial).format('DD/MM/YYYY'),
        dataFinal: dayjs(dataFinal).format('DD/MM/YYYY'),
        periodo
      };

      // Calcula métricas atualizadas
      const metricasAtuais = calcularMetricas(dadosRelatorio);

      // Prepara dados para o HeaderPadrao conforme solicitado
      const empresaValue = user?.assignedBranch?.codigo || user?.empresa || empresaInicial;
      const navbarInfo = {
        empresa: user?.assignedBranch?.codigo || user?.empresa,
        sigla: user?.assignedBranch?.sigla || user?.assignedBranch?.acronym,
        nome: user?.nome || user?.name
      };

      // Gera o HTML do relatório usando a função utilitária (agora assíncrona)
      const reportHTML = await generatePrintableHTML({
        title: 'Relatório Gerencial - Painel Operacional',
        organizationName: 'EtiquetandoAPP',
        data: dadosRelatorio,
        periodo: periodo,
        filtros: filtros,
        metricas: metricasAtuais,
        topVeiculos: topVeiculos,
        topClientes: topClientes,
        topImpressoras: topImpressoras,
        dailyAgg: dailyAgg,
        includeCharts: true,
        empresa: empresaValue,
        navbarInfo: navbarInfo
      });

      // Remove loading
      loadingMessage();

      // Cria uma nova janela para o relatório
      const printWindow = window.open('', '_blank', 'width=800,height=600');

      if (!printWindow) {
        message.error('Não foi possível abrir a janela de impressão. Verifique se o bloqueador de pop-ups está desabilitado.');
        return;
      }

      // Escreve o HTML na nova janela
      printWindow.document.write(reportHTML);
      printWindow.document.close();

      // Aguarda o carregamento e abre o diálogo de impressão
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
        }, 500);
      };

      message.success('Relatório gerado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar relatório para impressão:', error);
      message.error('Erro ao gerar relatório para impressão');
    }
  }

  // Renderização embutida: retorna apenas header + conteúdo
  if (embedded) {
    return (
      <>
        <div className="content-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 24px',
          borderBottom: currentTheme === 'dark' ? '1px solid #333333' : '1px solid #f0f0f0',
          backgroundColor: currentTheme === 'dark' ? '#111111' : undefined
        }}>
          <Title level={2} style={{
            margin: 0,
            color: currentTheme === 'dark' ? '#ffffff' : undefined
          }}>Relatórios</Title>
        </div>

        <div style={{
          padding: '12px',
          backgroundColor: currentTheme === 'dark' ? '#000000' : '#f5f5f5',
          minHeight: 'calc(100vh - 104px)'
        }}>
          <Card
            title="Filtros para Geração de Relatórios"
            style={{
              backgroundColor: currentTheme === 'dark' ? '#1f1f1f' : '#ffffff',
              border: currentTheme === 'dark' ? '1px solid #333333' : '1px solid #d9d9d9'
            }}
            styles={{
              header: {
                backgroundColor: currentTheme === 'dark' ? '#1f1f1f' : '#ffffff',
                color: currentTheme === 'dark' ? '#ffffff' : '#000000',
                borderBottom: currentTheme === 'dark' ? '1px solid #333333' : '1px solid #d9d9d9',
                fontSize: '12px',
                padding: '8px 16px',
                minHeight: '32px'
              },
              body: {
                backgroundColor: currentTheme === 'dark' ? '#1f1f1f' : '#ffffff',
                padding: '12px'
              }
            }}
          >
            <Row gutter={[8, 8]} align="bottom">
              <Col xs={24} sm={12} md={4}>
                <div style={{ marginBottom: 4 }}>
                  <label style={{
                    color: currentTheme === 'dark' ? '#ffffff' : '#000000',
                    fontSize: '10px',
                    fontWeight: '500'
                  }}>
                    Empresa Inicial:
                  </label>
                </div>
                <Input
                  value={empresaInicial}
                  onChange={(e) => setEmpresaInicial(e.target.value)}
                  placeholder="1"
                  size="small"
                  style={{
                    backgroundColor: currentTheme === 'dark' ? '#2f2f2f' : '#ffffff',
                    borderColor: currentTheme === 'dark' ? '#444444' : '#d9d9d9',
                    color: currentTheme === 'dark' ? '#ffffff' : '#000000',
                    fontSize: '10px'
                  }}
                />
              </Col>

              <Col xs={24} sm={12} md={4}>
                <div style={{ marginBottom: 4 }}>
                  <label style={{
                    color: currentTheme === 'dark' ? '#ffffff' : '#000000',
                    fontSize: '10px',
                    fontWeight: '500'
                  }}>
                    Empresa Final:
                  </label>
                </div>
                <Input
                  value={empresaFinal}
                  onChange={(e) => setEmpresaFinal(e.target.value)}
                  placeholder="999"
                  size="small"
                  style={{
                    backgroundColor: currentTheme === 'dark' ? '#2f2f2f' : '#ffffff',
                    borderColor: currentTheme === 'dark' ? '#444444' : '#d9d9d9',
                    color: currentTheme === 'dark' ? '#ffffff' : '#000000',
                    fontSize: '10px'
                  }}
                />
              </Col>

              <Col xs={24} sm={12} md={4}>
                <div style={{ marginBottom: 4 }}>
                  <label style={{
                    color: currentTheme === 'dark' ? '#ffffff' : '#000000',
                    fontSize: '10px',
                    fontWeight: '500'
                  }}>
                    Data Inicial:
                  </label>
                </div>
                <DatePicker
                  value={dataInicial ? dayjs(dataInicial) : null}
                  onChange={(date) => setDataInicial(date ? date.format('YYYY-MM-DD') : '')}
                  format="DD/MM/YYYY"
                  placeholder="17/10/2025"
                  size="small"
                  style={{
                    width: '100%',
                    backgroundColor: currentTheme === 'dark' ? '#2f2f2f' : '#ffffff',
                    borderColor: currentTheme === 'dark' ? '#444444' : '#d9d9d9',
                    color: currentTheme === 'dark' ? '#ffffff' : '#000000',
                    fontSize: '10px'
                  }}
                />
              </Col>

              <Col xs={24} sm={12} md={4}>
                <div style={{ marginBottom: 4 }}>
                  <label style={{
                    color: currentTheme === 'dark' ? '#ffffff' : '#000000',
                    fontSize: '10px',
                    fontWeight: '500'
                  }}>
                    Data Final:
                  </label>
                </div>
                <DatePicker
                  value={dataFinal ? dayjs(dataFinal) : null}
                  onChange={(date) => setDataFinal(date ? date.format('YYYY-MM-DD') : '')}
                  format="DD/MM/YYYY"
                  placeholder="17/10/2025"
                  size="small"
                  style={{
                    width: '100%',
                    backgroundColor: currentTheme === 'dark' ? '#2f2f2f' : '#ffffff',
                    borderColor: currentTheme === 'dark' ? '#444444' : '#d9d9d9',
                    color: currentTheme === 'dark' ? '#ffffff' : '#000000',
                    fontSize: '10px'
                  }}
                />
              </Col>

              <Col xs={24} sm={24} md={8}>
                <Space>
                  <Button
                    type="primary"
                    icon={<BarChartOutlined />}
                    onClick={handleGerarRelatorio}
                    loading={loading}
                    size="small"
                    style={{
                      backgroundColor: '#4f46e5',
                      borderColor: '#4f46e5',
                      fontSize: '10px'
                    }}
                  >
                    Gerar Relatórios
                  </Button>

                  <Button
                    icon={<PrinterOutlined />}
                    onClick={handleImprimir}
                    size="small"
                    style={{
                      backgroundColor: currentTheme === 'dark' ? '#2f2f2f' : '#ffffff',
                      borderColor: currentTheme === 'dark' ? '#444444' : '#d9d9d9',
                      color: currentTheme === 'dark' ? '#ffffff' : '#000000',
                      fontSize: '10px'
                    }}
                  >
                    Imprimir
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>

          <MetricsGrid
            metrics={metricas}
            theme={currentTheme}
            onExport={() => message.info('Funcionalidade de exportação será implementada')}
          />

          {/* Gráficos FILIAL e CARRO */}
          <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
            <Col xs={24} lg={12}>
              <FilialEtiquetasCard dados={dadosRelatorio} theme={currentTheme} />
            </Col>
            <Col xs={24} lg={12}>
              <CarroFilialEtiquetasCard dados={dadosRelatorio} theme={currentTheme} />
            </Col>
          </Row>

          {/* Gráfico de Filiais por Inclusões Manuais */}
          <FiliaisInclusaoManualCard dados={dadosRelatorio} theme={currentTheme} />

          {/* Série temporal diária + média móvel */}
          <DailySeriesCard daily={dailyAgg} dados={dadosRelatorio} theme={currentTheme} />

          {/* Barras empilhadas por dia (Impressas vs Pendentes) */}
          <DailyStackedCard daily={dailyAgg} dados={dadosRelatorio} theme={currentTheme} />

          {/* operação por Hora */}
          <OperacaoPorHoraCard dados={dadosRelatorio} theme={currentTheme} />

          {/* Heatmap Dia/Hora */}
          <HeatmapCard dados={dadosRelatorio} theme={currentTheme} />
          {/* Top 10 Seções */}
          <Top10Card
            title="Top 10 Veículos (Etiquetas)"
            icon={<TruckOutlined />}
            items={topVeiculos}
            theme={currentTheme}
            color="#6366f1"
            onExport={() => exportTop10XLSX([{ name: 'Veiculos', items: topVeiculos }])}
          />

          <Top10Card
            title="Top 10 Clientes (Etiquetas)"
            icon={<UserOutlined />}
            items={topClientes}
            theme={currentTheme}
            color="#10b981"
            onExport={() => exportTop10XLSX([{ name: 'Clientes', items: topClientes }])}
          />

          <Top10Card
            title="Top 10 Impressoras (Etiquetas)"
            icon={<PrinterOutlined />}
            items={topImpressoras}
            theme={currentTheme}
            color="#8b5cf6"
            onExport={() => exportTop10XLSX([{ name: 'Impressoras', items: topImpressoras }])}
          />
        </div>
      </>
    )
  }

  // Fallback para não-embutido
  return null
}

// ===== utils de séries diárias =====
const AJUSTE_HORAS_DATA_RELATORIO = 3

const normalizarDataRelatorio = (valor) => {
  if (!valor) return null
  if (typeof valor === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(valor)) {
    const data = dayjs(valor)
    return data.isValid() ? data : null
  }
  const data = dayjs(valor)
  if (!data.isValid()) return null
  if (typeof valor === 'string' && /T/.test(valor) && valor.endsWith('Z')) {
    return data.add(AJUSTE_HORAS_DATA_RELATORIO, 'hour')
  }
  return data
}

const obterChaveDiaRelatorio = (valor) => {
  const dataNormalizada = normalizarDataRelatorio(valor)
  return dataNormalizada ? dataNormalizada.format('YYYY-MM-DD') : null
}

const movingAvg = (arr, window = 7) => {
  const res = []
  let sum = 0
  for (let i = 0; i < arr.length; i++) {
    sum += arr[i]
    if (i >= window) sum -= arr[i - window]
    const len = i + 1 < window ? i + 1 : window
    res.push(+((sum / len).toFixed(2)))
  }
  return res
}

const groupByDate = (dados) => {
  const arr = Array.isArray(dados) ? dados : []
  const map = new Map()
  for (const item of arr) {
    const dt = item.DATA ?? item.data ?? item.DATA_EMISSAO ?? item.dataEmissao
    const day = obterChaveDiaRelatorio(dt)
    if (!day) continue
    const current = map.get(day) || { notas: 0, etiquetasImp: 0, etiquetasSem: 0, volumes: 0, peso: 0 }
    current.notas += 1
    current.etiquetasImp += toNumber(item.TTL_ETIQUETAS_IMP ?? item.TTL_ETIQUETAS ?? item.etiquetas_imp)
    current.etiquetasSem += toNumber(item.TTL_ETIQUETAS_SEM ?? item.etiquetas_sem)
    current.volumes += toNumber(item.VOLUMES ?? item.volumes ?? item.volumesoriginal)
    current.peso += toNumber(item.PESO ?? item.peso)
    map.set(day, current)
  }
  return Array.from(map.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([day, v]) => ({ day, ...v }))
}

const buildDailySeriesData = (daily, theme) => {
  const labels = daily.map(d => dayjs(d.day).format('DD/MM'))
  const etiquetasDiaTotal = daily.map(d => (d.etiquetasImp + d.etiquetasSem))
  const ma7 = movingAvg(etiquetasDiaTotal, 7)
  return {
    data: {
      labels,
      datasets: [
        {
          type: 'bar',
          label: 'Etiquetas/dia (Total)',
          data: etiquetasDiaTotal,
          backgroundColor: theme === 'dark' ? 'rgba(59,130,246,0.6)' : 'rgba(59,130,246,0.6)',
          borderColor: theme === 'dark' ? '#93c5fd' : '#3b82f6'
        },
        {
          type: 'line',
          label: 'Média móvel Etiquetas (7d)',
          yAxisID: 'y1',
          data: ma7,
          borderColor: theme === 'dark' ? '#10b981' : '#22c55e',
          backgroundColor: theme === 'dark' ? '#10b981' : '#22c55e',
          tension: 0.3,
          pointRadius: 2
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'bottom' },
        title: { display: true, text: 'Volume Diário + Média Móvel' }
      },
      scales: {
        x: { ticks: { color: theme === 'dark' ? '#e5e7eb' : '#111827' } },
        y: { ticks: { color: theme === 'dark' ? '#e5e7eb' : '#111827' } },
        y1: { position: 'right', grid: { drawOnChartArea: false }, ticks: { color: theme === 'dark' ? '#e5e7eb' : '#111827' } }
      }
    }
  }
}

const buildStackedDailyData = (daily, theme) => {
  const labels = daily.map(d => dayjs(d.day).format('DD/MM'))
  const impressas = daily.map(d => d.etiquetasImp)
  const pendentes = daily.map(d => d.etiquetasSem)
  return {
    data: {
      labels,
      datasets: [
        {
          type: 'bar',
          label: 'Impressas',
          data: impressas,
          backgroundColor: theme === 'dark' ? 'rgba(34,197,94,0.7)' : 'rgba(16,185,129,0.7)'
        },
        {
          type: 'bar',
          label: 'Pendentes',
          data: pendentes,
          backgroundColor: theme === 'dark' ? 'rgba(245,158,11,0.7)' : 'rgba(245,158,11,0.7)'
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'bottom' },
        title: { display: true, text: 'Impressas vs Pendentes por Dia' }
      },
      scales: {
        x: { stacked: true, ticks: { color: theme === 'dark' ? '#e5e7eb' : '#111827' } },
        y: { stacked: true, ticks: { color: theme === 'dark' ? '#e5e7eb' : '#111827' } }
      }
    }
  }
}

const DailySeriesCard = ({ daily, dados, theme }) => {
  const { data, options } = buildDailySeriesData(daily, theme)

  // Calcula estatísticas do período
  const stats = useMemo(() => {
    if (!dados || dados.length === 0) return { total: 0, media: 0, dias: 0 }

    const total = dados.reduce((sum, item) => {
      const etiquetasImp = toNumber(item.TTL_ETIQUETAS_IMP || item.etiquetasImp || 0)
      const etiquetasSem = toNumber(item.TTL_ETIQUETAS_SEM || item.etiquetasSem || 0)
      return sum + etiquetasImp + etiquetasSem
    }, 0)

    const diasUnicos = new Set(dados.map(item => {
      const data = item.DATA || item.data || item.DT_EMISSAO || item.dt_emissao
      return obterChaveDiaRelatorio(data)
    }).filter(Boolean)).size

    const media = diasUnicos > 0 ? total / diasUnicos : 0

    return { total, media, dias: diasUnicos }
  }, [dados])

  const titleWithStats = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
      <span>Volume Diário + Média Móvel</span>
      <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: theme === 'dark' ? '#8c8c8c' : '#666' }}>
        <span>Total: <strong>{(stats?.total || 0).toLocaleString()}</strong></span>
        <span>Média: <strong>{Math.round(stats?.media || 0).toLocaleString()}/dia</strong></span>
        <span>({stats?.dias || 0} dias)</span>
      </div>
    </div>
  )

  return (
    <Card
      title={titleWithStats}
      style={{ borderRadius: 8, marginTop: 12 }}
      styles={{ body: { padding: 12 } }}
    >
      <div style={{ height: 320 }}>
        <Bar data={data} options={options} />
      </div>
    </Card>
  )
}

const DailyStackedCard = ({ daily, dados, theme }) => {
  const { data, options } = buildStackedDailyData(daily, theme)

  // Calcula estatísticas de impressas vs não impressas
  const stats = useMemo(() => {
    if (!dados || dados.length === 0) return { impressas: 0, naoImpressas: 0 }

    const impressas = dados.reduce((sum, item) => {
      const etiquetasImp = toNumber(item.TTL_ETIQUETAS_IMP || item.etiquetasImp || 0)
      return sum + etiquetasImp
    }, 0)

    const naoImpressas = dados.reduce((sum, item) => {
      const etiquetasSem = toNumber(item.TTL_ETIQUETAS_SEM || item.etiquetasSem || 0)
      return sum + etiquetasSem
    }, 0)

    return { impressas, naoImpressas }
  }, [dados])

  const titleWithStats = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
      <span>Impressas vs Pendentes por Dia</span>
      <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: theme === 'dark' ? '#8c8c8c' : '#666' }}>
        <span>Impressas: <strong style={{ color: '#52c41a' }}>{(stats?.impressas || 0).toLocaleString()}</strong></span>
        <span>Pendentes: <strong style={{ color: '#ff4d4f' }}>{(stats?.naoImpressas || 0).toLocaleString()}</strong></span>
        <span>Total: <strong>{((stats?.impressas || 0) + (stats?.naoImpressas || 0)).toLocaleString()}</strong></span>
      </div>
    </div>
  )

  return (
    <Card
      title={titleWithStats}
      style={{ borderRadius: 8, marginTop: 12 }}
      styles={{ body: { padding: 12 } }}
    >
      <div style={{ height: 320 }}>
        <Bar data={data} options={options} />
      </div>
    </Card>
  )
}

// ===== Funções para gráficos FILIAL e CARRO =====
const buildFiliaisInclusaoManualData = (dados, theme) => {
  if (!dados || dados.length === 0) return { data: { labels: [], datasets: [] }, options: {} }

  // Agrupa por filial e conta inclusões manuais
  const filiaisMap = new Map()

  dados.forEach(item => {
    if (item.inclusao === 'MANUAL') {
      const filial = item.FILIAL || item.filial || 'N/A'
      const current = filiaisMap.get(filial) || 0
      filiaisMap.set(filial, current + 1)
    }
  })

  // Converte para array e ordena por quantidade
  const filiaisArray = Array.from(filiaisMap.entries())
    .map(([filial, quantidade]) => ({ filial, quantidade }))
    .sort((a, b) => b.quantidade - a.quantidade)
    .slice(0, 10) // Top 10 filiais

  const labels = filiaisArray.map(item => item.filial)
  const data = filiaisArray.map(item => item.quantidade)

  return {
    data: {
      labels,
      datasets: [
        {
          label: 'Inclusões Manuais',
          data,
          backgroundColor: theme === 'dark' ? 'rgba(168,85,247,0.7)' : 'rgba(147,51,234,0.7)',
          borderColor: theme === 'dark' ? '#a855f7' : '#9333ea',
          borderWidth: 1
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'bottom' },
        title: { display: true, text: 'Top Filiais por Inclusões Manuais' }
      },
      scales: {
        x: {
          ticks: {
            color: theme === 'dark' ? '#e5e7eb' : '#111827',
            maxRotation: 45
          }
        },
        y: {
          ticks: {
            color: theme === 'dark' ? '#e5e7eb' : '#111827',
            stepSize: 1
          }
        }
      }
    }
  }
}

const FiliaisInclusaoManualCard = ({ dados, theme }) => {
  const { data, options } = buildFiliaisInclusaoManualData(dados, theme)

  // Calcula estatísticas de inclusões manuais
  const stats = useMemo(() => {
    if (!dados || dados.length === 0) return { total: 0, topFilial: 'N/A', topQuantidade: 0 }

    const filiaisMap = new Map()
    let total = 0

    dados.forEach(item => {
      if (item.inclusao === 'MANUAL') {
        total++
        const filial = item.FILIAL || item.filial || 'N/A'
        const current = filiaisMap.get(filial) || 0
        filiaisMap.set(filial, current + 1)
      }
    })

    // Encontra a filial com mais inclusões manuais
    let topFilial = 'N/A'
    let topQuantidade = 0

    for (const [filial, quantidade] of filiaisMap.entries()) {
      if (quantidade > topQuantidade) {
        topFilial = filial
        topQuantidade = quantidade
      }
    }

    return { total, topFilial, topQuantidade }
  }, [dados])

  const titleWithStats = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
      <span>Filiais por Inclusão Manual</span>
      <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: theme === 'dark' ? '#8c8c8c' : '#666' }}>
        <span>Total: <strong style={{ color: '#a855f7' }}>{(stats?.total || 0).toLocaleString()}</strong></span>
        <span>Top: <strong>{stats?.topFilial}</strong> ({stats?.topQuantidade})</span>
      </div>
    </div>
  )

  return (
    <Card
      title={titleWithStats}
      style={{ borderRadius: 8, marginTop: 12 }}
      styles={{ body: { padding: 12 } }}
    >
      <div style={{ height: 320 }}>
        <Bar data={data} options={options} />
      </div>
    </Card>
  )
}

const buildFilialChartData = (dados, theme) => {
  const filialAgg = aggregateBy(dados, (item) => {
    return item.FILIAL || item.filial || item.EMPRESA || item.empresa || 'N/A'
  })

  const sortedFiliais = filialAgg
    .map((item) => ({
      filial: item.label,
      total: item.etiquetasImp + item.etiquetasSem
    }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 12)

  return {
    labels: sortedFiliais.map(f => f.filial),
    datasets: [{
      label: 'Total Etiquetas',
      data: sortedFiliais.map(f => f.total),
      backgroundColor: '#ff8c00',
      borderColor: '#ff8c00',
      borderWidth: 1,
      borderRadius: 4
    }]
  }
}

const buildCarroFilialChartData = (dados, theme) => {
  // Primeiro, agrupa por carro individual para obter totais
  const carroAgg = aggregateBy(dados, (item) => {
    const filial = item.FILIAL || item.filial || item.EMPRESA || item.empresa || 'N/A'
    const carro = item.CARRO || item.veiculo || item.carro || 'N/A'
    return `${filial}|${carro}` // Usando | como separador para depois dividir
  })

  // Agrupa por filial e encontra o top 1 carro de cada filial
  const filialCarroMap = new Map()

  carroAgg.forEach((item) => {
    const [filial, carro] = item.label.split('|')
    const total = item.etiquetasImp + item.etiquetasSem

    if (!filialCarroMap.has(filial) || filialCarroMap.get(filial).total < total) {
      filialCarroMap.set(filial, {
        filial,
        carro,
        total,
        label: `${filial} - ${carro}`
      })
    }
  })

  // Converte para array e ordena por total
  const topCarrosPorFilial = Array.from(filialCarroMap.values())
    .sort((a, b) => b.total - a.total)
    .slice(0, 15)

  return {
    labels: topCarrosPorFilial.map(c => c.label),
    datasets: [{
      label: 'Total Etiquetas (Top 1 por Filial)',
      data: topCarrosPorFilial.map(c => c.total),
      backgroundColor: '#1890ff',
      borderColor: '#1890ff',
      borderWidth: 1,
      borderRadius: 4
    }]
  }
}

const FilialEtiquetasCard = ({ dados, theme }) => {
  const chartData = useMemo(() => buildFilialChartData(dados, theme), [dados, theme])

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { display: false }
    },
    scales: {
      x: {
        ticks: {
          color: theme === 'dark' ? '#e5e7eb' : '#111827',
          maxRotation: 45
        }
      },
      y: {
        ticks: { color: theme === 'dark' ? '#e5e7eb' : '#111827' }
      }
    }
  }

  return (
    <Card
      title="Etiquetas por Filial"
      style={{ borderRadius: 8, marginTop: 12 }}
      styles={{ body: { padding: 12 } }}
    >
      <div style={{ height: 320 }}>
        <Bar data={chartData} options={options} />
      </div>
    </Card>
  )
}

const CarroFilialEtiquetasCard = ({ dados, theme }) => {
  const chartData = useMemo(() => buildCarroFilialChartData(dados, theme), [dados, theme])

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      legend: { display: false },
      title: { display: false }
    },
    scales: {
      x: {
        ticks: { color: theme === 'dark' ? '#e5e7eb' : '#111827' }
      },
      y: {
        ticks: {
          color: theme === 'dark' ? '#e5e7eb' : '#111827',
          font: { size: 10 }
        }
      }
    }
  }

  return (
    <Card
      title="Top 1 Carro por Filial (Etiquetas)"
      style={{ borderRadius: 8, marginTop: 12 }}
      styles={{ body: { padding: 12 } }}
    >
      <div style={{ height: 320 }}>
        <Bar data={chartData} options={options} />
      </div>
    </Card>
  )
}

// ===== Utils de agregação e exportação =====
const toNumber = (v) => {
  try {
    if (typeof v === 'bigint') return Number(v)
    if (typeof v === 'string' && /\d+n/.test(v)) return Number(BigInt(v.replace('n', '')))
    const n = Number(v)
    return Number.isFinite(n) ? n : 0
  } catch {
    return 0
  }
}

const aggregateBy = (dados, keyResolver) => {
  const arr = Array.isArray(dados) ? dados : []
  const map = new Map()
  for (const item of arr) {
    const label = String(keyResolver(item) || '').trim()
    if (!label) continue
    const current = map.get(label) || { notas: 0, etiquetasImp: 0, etiquetasSem: 0, volumes: 0, peso: 0, m3: 0 }
    current.notas += 1
    current.etiquetasImp += toNumber(item.TTL_ETIQUETAS_IMP ?? item.TTL_ETIQUETAS ?? item.etiquetas_imp)
    current.etiquetasSem += toNumber(item.TTL_ETIQUETAS_SEM ?? item.etiquetas_sem)
    current.volumes += toNumber(item.VOLUMES ?? item.volumes ?? item.volumesoriginal)
    current.peso += toNumber(item.PESO ?? item.peso)
    current.m3 += toNumber(item.M3 ?? item.m3)
    map.set(label, current)
  }
  return Array.from(map.entries()).map(([label, v]) => ({ label, ...v }))
}

const buildChartData = (items, title, theme, color = '#6366f1') => {
  const labels = items.map(i => i.label)
  const values = items.map(i => i.etiquetasImp)
  const total = values.reduce((a, b) => a + b, 0) || 1
  let cum = 0
  const pareto = values.map(v => { cum += v; return +((cum / total * 100).toFixed(1)) })

  return {
    data: {
      labels,
      datasets: [
        {
          type: 'bar',
          label: 'Etiquetas',
          data: values,
          backgroundColor: theme === 'dark' ? 'rgba(99,102,241,0.6)' : 'rgba(79,70,229,0.6)',
          borderColor: color,
          borderWidth: 1
        },
        {
          type: 'line',
          label: 'Pareto %',
          yAxisID: 'y1',
          data: pareto,
          borderColor: theme === 'dark' ? '#10b981' : '#22c55e',
          backgroundColor: theme === 'dark' ? '#10b981' : '#22c55e',
          tension: 0.3,
          pointRadius: 2
        }
      ]
    },
    options: {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'bottom' },
        title: { display: true, text: title }
      },
      scales: {
        x: { ticks: { color: theme === 'dark' ? '#e5e7eb' : '#111827' } },
        y: { ticks: { color: theme === 'dark' ? '#e5e7eb' : '#111827' } },
        y1: { position: 'right', min: 0, max: 100, grid: { drawOnChartArea: false }, ticks: { color: theme === 'dark' ? '#e5e7eb' : '#111827', callback: v => `${v}%` } }
      }
    }
  }
}

const exportTop10XLSX = (sections) => {
  const wb = XLSX.utils.book_new()
  sections.forEach(({ name, items }) => {
    const rows = items.map(i => ({
      Item: i.label,
      Notas: i.notas,
      Etiquetas_Impressas: i.etiquetasImp,
      Etiquetas_Pendentes: i.etiquetasSem,
      Volumes: i.volumes,
      Peso: i.peso,
      M3: i.m3
    }))
    const ws = XLSX.utils.json_to_sheet(rows)
    XLSX.utils.book_append_sheet(wb, ws, name)
  })
  XLSX.writeFile(wb, `relatorios_top10_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`)
}

const Top10Card = ({ title, icon, items, theme, color = '#6366f1', onExport }) => {
  const { data, options } = buildChartData(items, title, theme, color)

  const columns = [
    { title: 'Item', dataIndex: 'label', key: 'label' },
    { title: 'Notas', dataIndex: 'notas', key: 'notas', sorter: (a, b) => a.notas - b.notas },
    { title: 'Etiquetas', dataIndex: 'etiquetasImp', key: 'etiquetasImp', sorter: (a, b) => a.etiquetasImp - b.etiquetasImp },
    { title: 'Pendentes', dataIndex: 'etiquetasSem', key: 'etiquetasSem', sorter: (a, b) => a.etiquetasSem - b.etiquetasSem },
    { title: 'Volumes', dataIndex: 'volumes', key: 'volumes', sorter: (a, b) => a.volumes - b.volumes },
    { title: 'Peso', dataIndex: 'peso', key: 'peso', sorter: (a, b) => a.peso - b.peso }
  ]

  return (
    <Card
      title={<span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{icon} {title}</span>}
      style={{ borderRadius: 8, marginTop: 12 }}
      styles={{ body: { padding: 12 } }}
    >
      <Row gutter={[12, 12]}>
        <Col xs={24} lg={12}>
          <div style={{ height: 320 }}>
            <Bar data={data} options={options} />
          </div>
        </Col>
        <Col xs={24} lg={12}>
          <div style={{ overflowX: 'auto' }}>
            <Table
              dataSource={items.map((i, idx) => ({ key: `${title}-${idx}`, ...i }))}
              columns={columns}
              size="small"
              pagination={{ pageSize: 10 }}
              scroll={{ x: 'max-content' }}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <Button size="small" onClick={onExport} icon={<DownloadOutlined />}>Exportar Top 10</Button>
          </div>
        </Col>
      </Row>
    </Card>
  )
}

export default Relatorios

// ===== Heatmap por Dia/Hora =====
const groupByDayHour = (dados) => {
  const arr = Array.isArray(dados) ? dados : []
  const daySet = new Set()
  const map = new Map() // key: weekdayLabel|hour -> volume
  const order = { 'Seg': 1, 'Ter': 2, 'Qua': 3, 'Qui': 4, 'Sex': 5, 'Sáb': 6, 'Dom': 7 }
  for (const item of arr) {
    const ts = item.PRIMEIRA_ETIQUETA ?? item.ULTIMA_ETIQUETA ?? item.DATA ?? item.DATA_EMISSAO ?? item.data ?? item.dataEmissao
    if (!ts) continue
    const etiquetasImpressas = toNumber(item.TTL_ETIQUETAS_IMP ?? 0)
    if (etiquetasImpressas <= 0) continue
    const d = dayjs(ts)
    const weekdayLabel = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'][d.day()]
    const hour = d.hour()
    daySet.add(weekdayLabel)
    const key = `${weekdayLabel}|${hour}`
    map.set(key, (map.get(key) || 0) + etiquetasImpressas)
  }
  const labelsX = Array.from(daySet).sort((a, b) => (order[a] ?? 99) - (order[b] ?? 99))
  const labelsY = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}h`)
  let max = 0
  labelsX.forEach(day => {
    for (let h = 0; h < 24; h++) {
      const v = map.get(`${day}|${h}`) || 0
      if (v > max) max = v
    }
  })
  return { labelsX, labelsY, map, max }
}

const groupByHourOperation = (dados) => {
  const arr = Array.isArray(dados) ? dados : []
  const totalsByHour = Array(24).fill(0)

  for (const item of arr) {
    const ts = item.PRIMEIRA_ETIQUETA ?? item.ULTIMA_ETIQUETA ?? item.DATA ?? item.DATA_EMISSAO ?? item.data ?? item.dataEmissao
    if (!ts) continue
    const etiquetasImpressas = toNumber(item.TTL_ETIQUETAS_IMP ?? 0)
    if (etiquetasImpressas <= 0) continue
    const d = dayjs(ts)
    if (!d.isValid()) continue
    const hour = d.hour()
    totalsByHour[hour] += etiquetasImpressas
  }

  return totalsByHour
}

const buildOperacaoPorHoraModel = (dados, theme) => {
  const totalsByHour = groupByHourOperation(dados)
  const width = 760
  const height = 260
  const left = 40
  const right = 40
  const baselineY = 125
  const amplitude = 74
  const scaleX = (hourOffset) => left + (hourOffset / 24) * (width - left - right)
  const buildY = (hourOffset) => baselineY - Math.sin((hourOffset / 24) * Math.PI * 2) * amplitude
  const timelineHours = Array.from({ length: 25 }, (_, i) => i)
  const dots = timelineHours.map((offset) => {
    const hour = (6 + offset) % 24
    const total = totalsByHour[hour] || 0
    const isDayPeriod = hour >= 6 && hour < 18

    return {
      offset,
      hour,
      x: scaleX(offset),
      y: buildY(offset),
      total,
      hasOperation: total > 0,
      isDayPeriod
    }
  })

  const curvePoints = Array.from({ length: 97 }, (_, i) => {
    const offset = i * 0.25
    return `${scaleX(offset)},${buildY(offset)}`
  }).join(' ')

  const labelMarkers = [
    { offset: 0, label: '06:00' },
    { offset: 6, label: '12:00' },
    { offset: 12, label: '18:00' },
    { offset: 18, label: '24:00' },
    { offset: 24, label: '06:00' }
  ].map((item) => ({
    ...item,
    x: scaleX(item.offset)
  }))

  return {
    totalsByHour,
    dots,
    curvePoints,
    labelMarkers,
    width,
    height,
    baselineY,
    colors: {
      backgroundTop: theme === 'dark' ? '#0b1018' : '#f8fafc',
      backgroundBottom: theme === 'dark' ? '#0e141f' : '#eef2ff',
      baseline: theme === 'dark' ? 'rgba(148,163,184,0.25)' : 'rgba(71,85,105,0.22)',
      guide: theme === 'dark' ? 'rgba(148,163,184,0.15)' : 'rgba(71,85,105,0.14)',
      line: theme === 'dark' ? '#54616f' : '#64748b',
      dotOnDay: theme === 'dark' ? 'rgba(254,240,138,0.85)' : 'rgba(254,240,138,0.72)',
      dotOnNight: theme === 'dark' ? 'rgba(254,240,138,0.85)' : 'rgba(254,240,138,0.72)',
      dotOnBorder: theme === 'dark' ? '#8fa0b3' : '#94a3b8',
      dotOff: theme === 'dark' ? '#626f7e' : '#cbd5e1',
      text: theme === 'dark' ? '#8b95a3' : '#526582',
      iconSun: '#f59e0b',
      iconMoon: '#fbbf24'
    }
  }
}

const OperacaoPorHoraCard = ({ dados, theme }) => {
  const model = useMemo(() => buildOperacaoPorHoraModel(dados, theme), [dados, theme])
  const totalComOperacao = model.totalsByHour.filter((v) => v > 0).length
  const gradientId = `operacao-por-hora-bg-${theme === 'dark' ? 'dark' : 'light'}`

  const title = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
      <span>operação por Hora</span>
      <span style={{ fontSize: '12px', color: theme === 'dark' ? '#8c8c8c' : '#666' }}>
        Horas com operação: <strong>{totalComOperacao}/24</strong>
      </span>
    </div>
  )

  return (
    <Card
      title={title}
      style={{ borderRadius: 8, marginTop: 12 }}
      styles={{ body: { padding: 12 } }}
    >
      <div style={{ height: 280 }}>
        <svg width="100%" height="100%" viewBox={`0 0 ${model.width} ${model.height}`} role="img" aria-label="operação por Hora">
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={model.colors.backgroundTop} />
              <stop offset="100%" stopColor={model.colors.backgroundBottom} />
            </linearGradient>
          </defs>
          <rect x="0" y="0" width={model.width} height={model.height} rx="8" fill={`url(#${gradientId})`} />
          <line x1={40} y1={model.baselineY} x2={model.width - 40} y2={model.baselineY} stroke={model.colors.baseline} strokeWidth="1.2" strokeDasharray="2 4" />
          {model.labelMarkers.map((marker) => (
            <line key={`guide-${marker.offset}`} x1={marker.x} y1={22} x2={marker.x} y2={model.height - 40} stroke={model.colors.guide} strokeWidth="1" strokeDasharray="1.5 5" />
          ))}
          <polyline fill="none" points={model.curvePoints} stroke={model.colors.line} strokeWidth="2.4" strokeLinecap="round" />
          {model.dots.map((dot) => (
            <g key={`dot-${dot.offset}`}>
              <circle
                cx={dot.x}
                cy={dot.y}
                r={dot.hasOperation ? 8 : 7}
                fill={dot.hasOperation ? (dot.isDayPeriod ? model.colors.dotOnDay : model.colors.dotOnNight) : model.colors.dotOff}
                stroke={dot.hasOperation ? model.colors.dotOnBorder : 'none'}
                strokeWidth={dot.hasOperation ? 1.4 : 0}
              >
                <title>{`${String(dot.hour).padStart(2, '0')}:00 - ${dot.hasOperation ? `operação (${dot.total.toLocaleString()} etiquetas)` : 'Sem operação'}`}</title>
              </circle>
            </g>
          ))}
          {model.labelMarkers.map((marker) => (
            <text key={`label-${marker.offset}`} x={marker.x} y={model.baselineY + 22} textAnchor="middle" fill={model.colors.text} fontSize="26" fontWeight="700" fontFamily="Arial, sans-serif">
              {marker.label}
            </text>
          ))}
          <text x={model.labelMarkers[0].x} y={model.baselineY + 2} textAnchor="middle" fill={model.colors.iconSun} fontSize="16">{"\u2600"}</text>
          <text x={model.labelMarkers[2].x} y={model.baselineY + 2} textAnchor="middle" fill={model.colors.iconSun} fontSize="16">{"\u2600"}</text>
          <text x={model.labelMarkers[4].x} y={model.baselineY + 2} textAnchor="middle" fill={model.colors.iconSun} fontSize="16">{"\u2600"}</text>
          <text x={model.labelMarkers[1].x} y={model.baselineY - 88} textAnchor="middle" fill={model.colors.iconSun} fontSize="18">{"\u2600"}</text>
          <text x={model.labelMarkers[3].x} y={model.baselineY + 94} textAnchor="middle" fill={model.colors.iconMoon} fontSize="28">{"\u263e"}</text>
        </svg>
      </div>
    </Card>
  )
}

const heatColor = (v, max, theme) => {
  const t = max ? Math.max(0, Math.min(1, v / max)) : 0
  // Paleta de calor quente: amarelo -> laranja -> vermelho
  // Stops em RGB para interpolar suavemente
  const stops = [
    [255, 247, 194], // amarelo bem claro
    [253, 219, 126], // amarelo
    [245, 178, 85],  // laranja
    [239, 108, 57],  // laranja forte
    [230, 69, 44],   // vermelho
    [184, 30, 30],   // vermelho escuro
  ]
  const n = stops.length - 1
  const i = Math.floor(t * n)
  const frac = t * n - i
  const c1 = stops[i]
  const c2 = stops[Math.min(i + 1, n)]
  const r = Math.round(c1[0] + (c2[0] - c1[0]) * frac)
  const g = Math.round(c1[1] + (c2[1] - c1[1]) * frac)
  const b = Math.round(c1[2] + (c2[2] - c1[2]) * frac)
  const alpha = theme === 'dark' ? 0.9 : 0.85
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

const buildHeatmapDataFromDados = (dados, theme) => {
  const { labelsX, labelsY, map, max } = groupByDayHour(dados)
  const dataPoints = []
  for (const day of labelsX) {
    for (let h = 0; h < 24; h++) {
      const v = map.get(`${day}|${h}`) || 0
      dataPoints.push({ x: day, y: `${String(h).padStart(2, '0')}h`, v })
    }
  }
  return {
    data: {
      datasets: [
        {
          label: 'Etiquetas por Dia/Hora',
          data: dataPoints,
          backgroundColor: (ctx) => {
            const v = ctx.dataset.data[ctx.dataIndex]?.v || 0
            return heatColor(v, max, theme)
          },
          borderWidth: 1,
          width: (ctx) => {
            const area = ctx.chart.chartArea
            return area ? Math.max(16, area.width / (labelsX.length || 1)) : 18
          },
          height: (ctx) => {
            const area = ctx.chart.chartArea
            return area ? Math.max(16, area.height / (labelsY.length || 1)) : 18
          }
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        title: { display: true, text: 'Heatmap (Dia x Hora)' },
        tooltip: {
          callbacks: {
            title: (items) => items?.[0]?.raw ? `${items[0].raw.x} - ${items[0].raw.y}` : '',
            label: (item) => `Etiquetas: ${item.raw?.v ?? 0}`
          }
        }
      },
      scales: {
        x: { type: 'category', labels: labelsX, ticks: { color: theme === 'dark' ? '#e5e7eb' : '#111827' } },
        y: { type: 'category', labels: labelsY, ticks: { color: theme === 'dark' ? '#e5e7eb' : '#111827' } }
      }
    }
  }
}

const HeatmapCard = ({ dados, theme }) => {
  const { data, options } = buildHeatmapDataFromDados(dados, theme)

  // Calcula o horário com mais impressões
  const peakHourInfo = useMemo(() => {
    if (!dados || !Array.isArray(dados) || dados.length === 0) {
      return {
        hour: 'N/A',
        total: 0,
        peakSpecific: 'N/A',
        peakValue: 0
      }
    }

    const { map } = groupByDayHour(dados)

    if (!map || map.size === 0) {
      return {
        hour: 'N/A',
        total: 0,
        peakSpecific: 'N/A',
        peakValue: 0
      }
    }

    let maxVolume = 0
    let peakHour = 'N/A'
    let peakDay = 'N/A'

    // Agrupa por hora (somando todos os dias)
    const hourTotals = new Map()

    for (const [key, volume] of map.entries()) {
      const [day, hour] = key.split('|')
      const hourNum = parseInt(hour)
      const currentTotal = hourTotals.get(hourNum) || 0
      hourTotals.set(hourNum, currentTotal + volume)

      // Também verifica o pico absoluto (dia + hora específicos)
      if (volume > maxVolume) {
        maxVolume = volume
        peakHour = `${String(hourNum).padStart(2, '0')}h`
        peakDay = day
      }
    }

    // Encontra a hora com maior total (somando todos os dias)
    let maxHourTotal = 0
    let bestHour = 'N/A'

    for (const [hour, total] of hourTotals.entries()) {
      if (total > maxHourTotal) {
        maxHourTotal = total
        bestHour = `${String(hour).padStart(2, '0')}h`
      }
    }

    return {
      hour: bestHour || 'N/A',
      total: maxHourTotal || 0,
      peakSpecific: `${peakDay} ${peakHour}`,
      peakValue: maxVolume || 0
    }
  }, [dados])

  const titleWithStats = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
      <span>Heatmap (Dia x Hora)</span>
      <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: theme === 'dark' ? '#8c8c8c' : '#666' }}>
        <span>Pico: <strong style={{ color: '#ff7a45' }}>{peakHourInfo?.hour || 'N/A'}</strong></span>
        <span>({(peakHourInfo?.total || 0).toLocaleString()} etiquetas)</span>
        <span>Máximo: <strong>{peakHourInfo?.peakSpecific || 'N/A'}</strong> ({(peakHourInfo?.peakValue || 0).toLocaleString()})</span>
      </div>
    </div>
  )

  return (
    <Card
      title={titleWithStats}
      style={{ borderRadius: 8, marginTop: 12 }}
      styles={{ body: { padding: 12 } }}
    >
      <div style={{ height: 360 }}>
        <ReactChart type="matrix" data={data} options={options} />
      </div>
    </Card>
  )
}
